import { useCallback, useEffect, useState } from 'react';
import { mensagemDoErro } from './erros.js';

/**
 * Uma consulta ao middleware, com a última resposta vencendo.
 *
 * É o ÚNICO carregamento do painel: listagem paginada, histórico de produto e
 * as listas que alimentam um filtro passam todas por aqui. Antes eram dois
 * ganchos com regras diferentes, e a mesma falha de rede aparecia com botão de
 * tentar de novo numa tela e sem botão nenhum na outra.
 *
 * Estados possíveis, e o que cada um afirma:
 *
 * | `dados` | `erro` | significado |
 * |---|---|---|
 * | `null` | `null` | ainda não se sabe — a tela NÃO pode dizer que está vazio |
 * | `null` | texto  | não deu para perguntar — oferecer tentar de novo |
 * | valor   | `null` | é isto o que o middleware respondeu |
 * | valor   | `null` + `carregando` | resposta anterior, enquanto a nova não chega |
 */
export type Consulta<T> = {
  readonly dados: T | null;
  readonly carregando: boolean;
  /** Texto pronto para a tela — exceção arbitrária nunca chega aqui. */
  readonly erro: string | null;
  /** Refaz a consulta com o mesmo recorte: "Tentar de novo" e toda escrita. */
  readonly recarregar: () => void;
};

type Estado<T> = Omit<Consulta<T>, 'recarregar'>;

const OCIOSA = { dados: null, carregando: false, erro: null } as const;

/**
 * `buscar` nulo significa "ainda não há o que consultar" (rastreio sem código
 * digitado, por exemplo) — e nesse caso nenhuma requisição sai.
 *
 * A função precisa ser estável (`useCallback`/`useMemo` sobre o recorte): é a
 * identidade dela que decide quando vale a pena perguntar de novo. Resposta
 * que chega depois de o recorte ter mudado é descartada pela limpeza do efeito
 * anterior — sem isso, digitar rápido na busca deixaria a tabela exibindo o
 * resultado de um termo que já não está no campo.
 */
export function useConsulta<T>(buscar: (() => Promise<T>) | null): Consulta<T> {
  const [estado, definirEstado] = useState<Estado<T>>(() =>
    buscar === null ? OCIOSA : { dados: null, carregando: true, erro: null },
  );
  const [tentativa, definirTentativa] = useState(0);

  useEffect(() => {
    if (buscar === null) {
      definirEstado(OCIOSA);
      return;
    }

    let vigente = true;
    // Mantém os dados anteriores na tela enquanto a nova resposta não chega:
    // piscar a tabela em branco a cada tecla digitada é pior que esperar.
    definirEstado((anterior) => ({ ...anterior, carregando: true, erro: null }));

    buscar().then(
      (dados) => {
        if (vigente) {
          definirEstado({ dados, carregando: false, erro: null });
        }
      },
      (erro: unknown) => {
        if (vigente) {
          // A resposta anterior é descartada junto: manter a lista velha sob
          // uma falha faria o supervisor decidir sobre um armazém que ele já
          // não conseguiu consultar.
          definirEstado({ dados: null, carregando: false, erro: mensagemDoErro(erro) });
        }
      },
    );

    return () => {
      vigente = false;
    };
  }, [buscar, tentativa]);

  const recarregar = useCallback(() => {
    definirTentativa((numero) => numero + 1);
  }, []);

  return { ...estado, recarregar };
}
